import { formatDateNumber } from '@seen/core/dates'
import { referenceDisplay } from '@seen/core/display'
import { featureFlags } from '@seen/core/featureFlags'
import { ConditionClinicalStatus } from '@seen/core/models/conditions/constants'
import { Diagnosis } from '@seen/core/models/conditions/Diagnosis'
import { VisitNote } from '@seen/core/models/visitNote'
import { resolveId } from '@seen/core/references'
import { isOfType } from '@seen/core/utils'
import { cn, DotSeparator } from '@seen/ui'
import { IconChevronDown, IconChevronRight } from '@tabler/icons-react'
import type { JSX } from 'react'
import { useMemo, useState } from 'react'
import { ConditionCard } from '#src/components/condition/ConditionCard.tsx'
import { HoverCard } from '#src/components/core/hover-card/HoverCard.tsx'
import { HoverCardContent } from '#src/components/core/hover-card/HoverCardContent.tsx'
import { HoverCardTrigger } from '#src/components/core/hover-card/HoverCardTrigger.tsx'
import { TextLink } from '#src/components/core/TextLink.tsx'
import { useSearch } from '#src/hooks/index.ts'
import { useFeatureFlag } from '#src/hooks/useFeatureFlag.ts'

interface VisitNoteConditionsWidgetProps {
  visitNote: VisitNote
  patientId: string
}

function ConditionRow({
  notes,
  children,
}: {
  notes: string[]
  children: JSX.Element
}): JSX.Element {
  const [expanded, setExpanded] = useState(false)
  const hasNotes = notes.length > 0

  return (
    <div>
      <div className="flex items-start gap-0.5">
        {hasNotes ? (
          <button
            type="button"
            className="mt-0.5 shrink-0 text-muted-foreground transition-colors hover:text-foreground"
            onClick={() => setExpanded((v) => !v)}
            aria-label={expanded ? 'Collapse notes' : 'Expand notes'}
          >
            {expanded ? (
              <IconChevronDown size={13} />
            ) : (
              <IconChevronRight size={13} />
            )}
          </button>
        ) : (
          <span className="w-[13px] shrink-0" />
        )}
        <div className="min-w-0">{children}</div>
      </div>
      {expanded && hasNotes && (
        <div className="space-y-1 mt-1 ml-4 border-l-2 border-muted pl-3">
          {notes.map((note, i) => (
            <p
              key={i}
              className="text-xs leading-relaxed whitespace-pre-wrap text-muted-foreground"
            >
              {note}
            </p>
          ))}
        </div>
      )}
    </div>
  )
}

export function VisitNoteConditionsWidget({
  visitNote,
  patientId,
}: VisitNoteConditionsWidgetProps): JSX.Element | null {
  const viewportLayout = useFeatureFlag(featureFlags.hoverCardViewportLayout)
  const conditionSections = useMemo(
    () =>
      Object.values(visitNote.sections)
        .flat()
        .filter((s) => VisitNote.isConditionSection(s)),
    [visitNote]
  )

  const conditionEntries = useMemo(
    () => conditionSections.flatMap((s) => s.entry ?? []),
    [conditionSections]
  )

  const notesByCondition = useMemo(() => {
    const noteSections = conditionSections.flatMap((s) => s.section ?? [])
    const map = new Map<string, string[]>()
    for (const ns of noteSections) {
      const key = ns.title ?? ''
      if (!key || !ns.text?.div) {
        continue
      }
      const existing = map.get(key) ?? []
      existing.push(ns.text.div)
      map.set(key, existing)
    }
    return map
  }, [conditionSections])

  const conditionIdString = useMemo(
    () =>
      conditionEntries
        .filter((e) => isOfType(e, 'Condition'))
        .map((r) => resolveId(r))
        .filter(Boolean)
        .join(','),
    [conditionEntries]
  )

  const conditionIds = useMemo(
    () => (conditionIdString ? conditionIdString.split(',') : []),
    [conditionIdString]
  )

  const displayOnlyConditions = useMemo(() => {
    const raw = conditionEntries
      .filter((e) => !isOfType(e, 'Condition') && referenceDisplay(e))
      .map((e) => referenceDisplay(e) ?? '')
      .join('\x00')
    return raw ? raw.split('\x00').filter(Boolean) : []
  }, [conditionEntries])

  const { data: conditions, isLoading: conditionsLoading } = useSearch(
    Diagnosis,
    conditionIds.length > 0 ? Diagnosis.queryByIds(conditionIds) : undefined
  )

  /** Filter out display-only entries that duplicate a loaded chart-level condition */
  const dedupedDisplayConditions = useMemo(() => {
    if (!conditions?.length) {
      return displayOnlyConditions
    }
    const loadedNames = new Set(conditions.map((c) => c.name.toLowerCase()))
    return displayOnlyConditions.filter(
      (name) => !loadedNames.has(name.toLowerCase())
    )
  }, [displayOnlyConditions, conditions])

  if (conditionIds.length === 0 && dedupedDisplayConditions.length === 0) {
    return null
  }

  const totalCount = (conditions?.length ?? 0) + dedupedDisplayConditions.length

  if (!conditionsLoading && totalCount === 0) {
    return null
  }

  return (
    <div className="rounded-md border">
      <div className="flex items-center justify-between border-b bg-muted/50 px-3 py-2">
        <h4 className="text-sm font-medium">Conditions</h4>
        {!conditionsLoading && totalCount > 0 && (
          <span className="text-xs text-muted-foreground">{totalCount}</span>
        )}
      </div>
      {conditionsLoading ? (
        <div className="px-3 py-4 text-sm text-muted-foreground">
          Loading...
        </div>
      ) : (
        <div className="flex flex-col gap-1 px-3 py-2 text-sm">
          {conditions?.map((condition) => {
            const entryDisplay = referenceDisplay(
              conditionEntries.find((e) => resolveId(e) === condition.id)
            )
            const notes =
              (entryDisplay ? notesByCondition.get(entryDisplay) : undefined) ??
              notesByCondition.get(condition.name) ??
              []
            return (
              <ConditionRow key={condition.id} notes={notes}>
                <HoverCard openDelay={300} closeDelay={200}>
                  <HoverCardTrigger asChild>
                    {/* w-fit keeps the card anchored to the condition name;
                        Radix measures the trigger's border box, so a full-width
                        row pushes a side="right" card to the far right edge and
                        starves it of width. */}
                    <p className={cn(viewportLayout && 'w-fit')}>
                      <TextLink
                        to={`/Patient/${patientId}/conditions/${condition.id}`}
                      >
                        {condition.name}
                      </TextLink>
                      {condition.onsetDateString && (
                        <span className="select-none">
                          <DotSeparator />
                          <span className="text-foreground-tertiary">
                            {formatDateNumber(condition.onsetDateString)}
                          </span>
                        </span>
                      )}
                      {condition.clinicalStatus ===
                        ConditionClinicalStatus.CONTROLLED && (
                        <span className="select-none">
                          <DotSeparator />
                          <span className="text-foreground-tertiary">
                            controlled
                          </span>
                        </span>
                      )}
                    </p>
                  </HoverCardTrigger>
                  <HoverCardContent
                    side="right"
                    align="start"
                    className="w-auto"
                  >
                    <ConditionCard
                      condition={condition}
                      to={`/Patient/${patientId}/conditions/${condition.id}`}
                    />
                  </HoverCardContent>
                </HoverCard>
              </ConditionRow>
            )
          })}
          {dedupedDisplayConditions.map((name, i) => {
            const notes = notesByCondition.get(name) ?? []
            return (
              <ConditionRow key={`display-${i}`} notes={notes}>
                <p className="text-muted-foreground">{name}</p>
              </ConditionRow>
            )
          })}
        </div>
      )}
    </div>
  )
}
